import { useTranslation } from 'react-i18next';
import { AlertTriangle, Wallet } from 'lucide-react';
import { TranslationNS } from '@gokrew/models/constants';
import type { LeaveApprovalProgress as ApprovalProgressData } from '@gokrew/models/approvals';
import type {
  LeaveBalanceCheck,
  LeaveDecisionContext,
  LeavePendingInsight,
  LeaveTeamAvailabilityEntry,
  LeaveWaitingOn,
  TeamOverlap,
} from '@gokrew/models/leave-management';
import { Tone } from '@gokrew/models/ui.types';
import { Chip } from '@/components/Chip';
import { cn } from '@/lib/utils';
import { formatDate } from '@/utility/utility';
import { LeaveAttachmentList } from '@/components/LeaveAttachments';
import styles from './LeaveDecision.module.css';

const ns = TranslationNS.LeaveManagement;
const key = (leaf: string) => `leave_decision.${leaf}`;

/** Days as the decision copy writes them ("1 day", "-1.5 days"). */
function useDays() {
  const { t } = useTranslation(ns);
  return (count: number) => t(key('days'), { count });
}

/**
 * Who a pending request waits on: a name, "HR", or a warning chip when
 * nobody can approve it. `ownerType` adds "Assigned approver" so a legacy
 * approver that outranks the reporting manager is visible as such.
 */
export function LeaveWaitingOnLabel({
  waitingOn,
}: {
  waitingOn: LeaveWaitingOn;
}) {
  const { t } = useTranslation(ns);
  if (waitingOn.kind === 'none') {
    return (
      <Chip type={Tone.WARNING} size="sm">
        {t(key('no_approver'))}
      </Chip>
    );
  }
  if (waitingOn.kind === 'hr') {
    return <span className={styles.primary}>{t(key('hr'))}</span>;
  }
  return (
    <span className={styles.stack}>
      <span className={styles.primary}>
        {waitingOn.name ?? t(key('approvers'))}
      </span>
      {waitingOn.ownerType === 'assigned_approver' && (
        <span className={styles.secondary}>{t(key('assigned_approver'))}</span>
      )}
      {waitingOn.ownerType === 'delegate' && (
        <span className={styles.secondary}>{t(key('delegate'))}</span>
      )}
    </span>
  );
}

/** "Pending 4 days", in the danger colour once it is overdue. */
export function LeavePendingDays({
  pendingDays,
  overdue,
  className,
}: {
  pendingDays: number;
  overdue: boolean;
  className?: string;
}) {
  const { t } = useTranslation(ns);
  return (
    <span
      className={cn(styles.secondary, overdue && styles.danger, className)}
      data-overdue={overdue || undefined}
    >
      {t(key(overdue ? 'pending_days_overdue' : 'pending_days'), {
        count: pendingDays,
      })}
    </span>
  );
}

/** The red "Insufficient balance" chip for a row. */
export function InsufficientBalanceChip() {
  const { t } = useTranslation(ns);
  return (
    <Chip type={Tone.ERROR} size="sm">
      {t(key('insufficient_chip'))}
    </Chip>
  );
}

/** The reporting manager with a "Temporary" chip, or "No manager". */
export function LeaveReportingManagerLabel({
  manager,
}: {
  manager: { name: string; isInterim: boolean } | null | undefined;
}) {
  const { t } = useTranslation(ns);
  if (!manager) {
    return <span className={styles.muted}>{t(key('no_manager'))}</span>;
  }
  return (
    <span className={styles.inline}>
      <span className={styles.primary}>{manager.name}</span>
      {manager.isInterim && (
        <Chip type={Tone.SOFT_BLUE} size="sm">
          {t(key('temporary'))}
        </Chip>
      )}
    </span>
  );
}

/**
 * The balance at decision time: a loud danger banner when the wallet cannot
 * cover the request, a quiet line otherwise.
 */
export function LeaveBalanceBanner({
  balance,
}: {
  balance: LeaveBalanceCheck | null | undefined;
}) {
  const { t } = useTranslation(ns);
  const days = useDays();
  if (!balance) return null;
  const figures = {
    available: days(balance.available),
    requested: days(balance.requested),
    after: days(balance.after),
  };
  if (balance.insufficient) {
    return (
      <div className={styles.banner} role="alert">
        <AlertTriangle className={styles.bannerIcon} aria-hidden="true" />
        <div className={styles.stack}>
          <span className={styles.bannerTitle}>
            {t(key('insufficient_title'))}
          </span>
          <span className={styles.bannerText}>
            {t(key('insufficient_text'), figures)}
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.balanceLine}>
      <Wallet className={styles.bannerIcon} aria-hidden="true" />
      <span>{t(key('balance_line'), figures)}</span>
    </div>
  );
}

function TeamRow({ entry }: { entry: LeaveTeamAvailabilityEntry }) {
  const { t, i18n } = useTranslation(ns);
  const opts = { format: 'D MMM', locale: i18n.language };
  const dates =
    entry.startDate === entry.endDate
      ? formatDate(entry.startDate, opts)
      : `${formatDate(entry.startDate, opts)} - ${formatDate(entry.endDate, opts)}`;
  return (
    <li className={styles.teamRow}>
      <span className={styles.stack}>
        <span className={styles.primary}>{entry.name}</span>
        <span className={styles.secondary}>
          {entry.relation === 'manager' ? `${t(key('manager'))} · ` : ''}
          {dates}
        </span>
      </span>
      <Chip
        type={entry.status === 'approved' ? Tone.SUCCESS : Tone.WARNING}
        size="sm"
      >
        {t(key(`team_status.${entry.status}`))}
      </Chip>
    </li>
  );
}

const STEP_TONE: Partial<Record<string, Tone>> = {
  approved: Tone.SUCCESS,
  rejected: Tone.ERROR,
  current: Tone.WARNING,
};

/**
 * Where a request is in its approval route (D186 to D190): "Step 2 of 3:
 * Mia Tremblay" while pending, each step with who decided it, skipped steps
 * ("nobody to ask"), the long leave reason for an added step, and approvals
 * given before an edit restarted the route. Nothing for a one-step route
 * with nothing to explain: the request's own status already says it all.
 */
export function LeaveApprovalProgress({
  approval,
}: {
  approval: ApprovalProgressData | null | undefined;
}) {
  const { t, i18n } = useTranslation(ns);
  if (!approval) return null;
  const superseded = approval.decisions.filter((d) => d.superseded);
  const skipped = approval.steps.some((s) => s.state === 'skipped');
  if (
    approval.steps.length <= 1 &&
    !skipped &&
    !superseded.length &&
    !approval.longLeaveApplied
  ) {
    return null;
  }
  const label = (leaf: string, opts?: Record<string, unknown>) =>
    t(`requests.approval.${leaf}`, opts);
  const when = (at: string) =>
    formatDate(at, { format: 'D MMM, HH:mm', locale: i18n.language });
  const kindLabel = (kind: string) => label(`step.${kind}`);
  const who =
    approval.currentKind && approval.currentKind !== 'hr'
      ? (approval.currentOwnerName ?? kindLabel(approval.currentKind))
      : approval.currentKind
        ? kindLabel(approval.currentKind)
        : null;

  return (
    <section className={styles.approval} aria-label={label('title')}>
      <div className={styles.approvalHead}>
        <span className={styles.label}>{label('title')}</span>
        {approval.current !== null && who && (
          <Chip type={Tone.SOFT_BLUE} size="sm">
            {label('step_chip', {
              current: approval.current,
              total: approval.total,
              who,
            })}
          </Chip>
        )}
      </div>
      {approval.longLeaveApplied && approval.longLeaveMinDays !== null && (
        <span className={styles.secondary}>
          {label('long_leave_reason', { days: approval.longLeaveMinDays })}
        </span>
      )}
      <ol className={styles.approvalSteps}>
        {approval.steps.map((step) => (
          <li key={step.step} className={styles.approvalStep}>
            <span className={styles.stack}>
              <span
                className={cn(
                  styles.primary,
                  (step.state === 'skipped' || step.state === 'not_reached') &&
                    styles.muted
                )}
              >
                {kindLabel(step.kind)}
              </span>
              {step.state === 'skipped' ? (
                <span className={styles.secondary}>{label('skipped')}</span>
              ) : (
                (step.by || step.at) && (
                  <span className={styles.secondary}>
                    {[step.by, step.at && when(step.at)]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                )
              )}
            </span>
            {STEP_TONE[step.state] && (
              <Chip type={STEP_TONE[step.state]!} size="sm">
                {label(`state.${step.state}`)}
              </Chip>
            )}
          </li>
        ))}
      </ol>
      {superseded.length > 0 && (
        <div className={styles.stack}>
          <span className={styles.secondary}>{label('superseded')}</span>
          <ul className={styles.approvalSteps}>
            {superseded.map((d, index) => (
              <li key={`${d.at}-${index}`} className={styles.approvalStep}>
                <span className={cn(styles.secondary, styles.struck)}>
                  {[
                    d.kind ? kindLabel(d.kind) : null,
                    label(`state.${d.decision}`),
                    d.by,
                    when(d.at),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export interface LeaveDecisionPanelProps {
  context: LeaveDecisionContext | undefined;
  isLoading?: boolean;
  /** Off when the host shows `LeaveBalanceBanner` itself (e.g. on top). */
  withBalance?: boolean;
}

/**
 * Who on the team is off during a request, in one compact line ("Team: 2 off
 * · Marc, Julie" / "Nobody else off"), shown wherever the request can be
 * approved or declined without opening anything (JP, 2026-10-01). First
 * names in the line; full names and dates on hover.
 */
export function TeamOverlapLine({
  overlap,
  className,
}: {
  overlap: TeamOverlap | null | undefined;
  className?: string;
}) {
  const { t, i18n } = useTranslation(ns);
  if (!overlap) return null;
  if (!overlap.count) {
    return (
      <span className={cn(styles.overlap, className)}>
        {t(key('team_overlap_none'))}
      </span>
    );
  }
  const opts = { format: 'D MMM', locale: i18n.language };
  const firstNames = overlap.people.map(
    (person) => person.name.split(' ')[0] || person.name
  );
  const more = overlap.count - overlap.people.length;
  const names =
    firstNames.join(', ') +
    (more > 0 ? ` ${t(key('team_overlap_more'), { count: more })}` : '');
  const title = overlap.people
    .map((person) =>
      person.startDate === person.endDate
        ? `${person.name} (${formatDate(person.startDate, opts)})`
        : `${person.name} (${formatDate(person.startDate, opts)} - ${formatDate(person.endDate, opts)})`
    )
    .join(', ');
  return (
    <span
      className={cn(styles.overlap, styles.overlapBusy, className)}
      title={title}
    >
      {t(key('team_overlap'), { count: overlap.count, names })}
    </span>
  );
}

/**
 * Everything a details modal adds to make a leave request decision-ready:
 * the balance banner first, then reporting manager, who it waits on and for
 * how long, the team's leave on those dates and the supporting documents.
 * Same in both portals.
 */
export function LeaveDecisionPanel({
  context,
  isLoading,
  withBalance = true,
}: LeaveDecisionPanelProps) {
  const { t, i18n } = useTranslation(ns);
  if (isLoading || !context) {
    return <div className={styles.skeleton} aria-busy="true" />;
  }
  const pending: LeavePendingInsight | null = context.pending;
  return (
    <div className={styles.panel}>
      {withBalance && <LeaveBalanceBanner balance={context.balanceCheck} />}
      <div className={styles.facts}>
        <div className={styles.fact}>
          <span className={styles.label}>{t(key('reporting_manager'))}</span>
          <LeaveReportingManagerLabel manager={context.reportingManager} />
        </div>
        {pending && (
          <div className={styles.fact}>
            <span className={styles.label}>{t(key('waiting_on'))}</span>
            <LeaveWaitingOnLabel waitingOn={pending.waitingOn} />
            <LeavePendingDays
              pendingDays={pending.pendingDays}
              overdue={pending.overdue}
            />
            {!!context.remindersSent && (
              <span
                className={styles.secondary}
                title={
                  context.lastReminderAt
                    ? t(key('last_reminder'), {
                        date: formatDate(context.lastReminderAt, {
                          format: 'D MMM, HH:mm',
                          locale: i18n.language,
                        }),
                      })
                    : undefined
                }
              >
                {t(key('reminders_sent'), { count: context.remindersSent })}
              </span>
            )}
          </div>
        )}
      </div>
      <section className={styles.team} aria-label={t(key('team_title'))}>
        <span className={styles.label}>{t(key('team_title'))}</span>
        {context.teamAvailability.length ? (
          <ul className={styles.teamList}>
            {context.teamAvailability.map((entry, index) => (
              <TeamRow key={`${entry.employeeId}-${index}`} entry={entry} />
            ))}
          </ul>
        ) : (
          <span className={styles.teamEmpty}>{t(key('team_empty'))}</span>
        )}
      </section>
      <LeaveApprovalProgress approval={context.approval} />
      <LeaveAttachmentList attachments={context.attachments} />
    </div>
  );
}
